import { httpResource } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';

import { ActivePartnerContext } from '../session/active-partner-context';
import { AUTH_CONFIG } from '../session/auth.config';
import { SUPER_ADMIN_ROLE } from '../session/session.types';
import { SessionService } from '../session/session.service';
import {
  type BlockingMembershipState,
  type MeCapabilitiesResponse,
  type PartnerMembershipState,
  isBlockingMembershipState,
  isPartnerMembershipState,
} from './membership.types';

/**
 * A user's standing in the active partner area — **which screen to render**,
 * not what they may do.
 *
 * Capability gating is untouched by this service and stays exactly where it
 * was: resolved synchronously from the `roles_token` through
 * `CapabilitiesService`, with `requireCapabilities`, `requirePartnerAccess` and
 * `*authHasCapability` reading it without ever awaiting a request. Deliberately
 * so — routing every capability check through a fetch would make the guards
 * async and put a network round trip in front of every partner-scoped
 * navigation.
 *
 * What the token *cannot* say is why a capability set is empty. Not a member,
 * seat suspended, invite unaccepted, or a seat with no role assigned all arrive
 * as `caps: []`, and they need four different things said to the person
 * looking at the screen. That is the only question this answers.
 *
 * **Never a hard gate.** Every failure mode — the field absent because the
 * backend predates it, an unrecognised value, an error, a request still in
 * flight — resolves to "render the app normally". Blocking someone out of a
 * working app because a diagnostic request failed would be a far worse bug than
 * the blank dashboard this exists to replace.
 */
@Injectable({ providedIn: 'root' })
export class PartnerMembershipService {
  private readonly session = inject(SessionService);
  /**
   * The active partner id comes from the context directly rather than through
   * `CapabilitiesService`, which only re-exports it. Keeps the two services
   * structurally independent, and keeps this one testable without stubbing
   * capability resolution.
   *
   * Note the sole-partner auto-select lives in the `CapabilitiesService`
   * constructor effect, so it only runs once something injects that service. In
   * partner-area the shell always does.
   */
  private readonly activePartner = inject(ActivePartnerContext);
  private readonly api = inject(AUTH_CONFIG).apiUrl;

  /**
   * The endpoint is scoped by the `creatorSub` **header**, so the URL is
   * byte-identical for every partner area.
   *
   * That makes the request object form mandatory rather than stylistic. The
   * string form produces a `computed<string>`, and its `===` equality means
   * switching partners would produce no new request at all — the service would
   * keep reporting the previous partner's membership indefinitely. Returning a
   * fresh object per id gives the resource something to see. (The auth
   * interceptor sets the same header from the same signal; `setHeaders`
   * overwrites with an identical value, so the duplicate is harmless and it
   * makes the scoping legible here rather than implicit two layers away.)
   *
   * Idle when no partner is active, and idle when `apiUrl` is unset — the
   * same inert-when-unconfigured convention the interceptor and route guards
   * follow, which is what keeps this invisible to every existing test.
   */
  private readonly resource = httpResource<MeCapabilitiesResponse>(() => {
    const id = this.activePartner.activePartnerId();
    if (!this.api || !id) {
      return undefined;
    }
    return { url: `${this.api}/v1/me/capabilities`, headers: { creatorSub: id } };
  });

  private readonly isSuperAdmin = computed(() => this.session.role() === SUPER_ADMIN_ROLE);

  /**
   * The reported state, or `null` when we do not have a trustworthy one.
   *
   * Gating on `isLoading()` covers `Reloading` as well as the first load, and
   * that second case is the one that bites: during a reload `value()` still
   * holds the *previous* partner's payload, so a member who is `active` in one
   * area and `no_role` in another would see the app for a beat after switching.
   */
  readonly state = computed<PartnerMembershipState | null>(() => {
    if (this.resource.isLoading() || this.resource.error()) {
      return null;
    }
    const raw = this.resource.value()?.membership;
    return isPartnerMembershipState(raw) ? raw : null;
  });

  /** The member's role name, when the API sends one. */
  readonly roleName = computed<string | null>(() => {
    if (this.resource.isLoading() || this.resource.error()) {
      return null;
    }
    return this.resource.value()?.roleName ?? null;
  });

  /**
   * The area's name, when the API sends one.
   *
   * `null` when `membership` is `none` — the backend withholds it there rather
   * than answering an unscoped `?creatorId=` with a partner's name. Every state
   * that renders a membership screen has an edge, so every one of them gets it.
   */
  readonly partnerName = computed<string | null>(() => {
    if (this.resource.isLoading() || this.resource.error()) {
      return null;
    }
    return this.resource.value()?.partnerName ?? null;
  });

  /** The area owner's address, when there is a resolvable one. */
  readonly ownerEmail = computed<string | null>(() => {
    if (this.resource.isLoading() || this.resource.error()) {
      return null;
    }
    return this.resource.value()?.ownerEmail ?? null;
  });

  /** Whether the membership request is in flight. */
  readonly loading = computed(() => this.resource.isLoading());

  /**
   * Whether the app should hold off rendering until the state is known.
   *
   * `blocked()` being false while loading is not enough on its own — false
   * means "render normally", so the router outlet would show the dashboard for
   * the length of the request and then swap it for the no-role screen. This is
   * the third branch that prevents the flash. A superAdmin is never held back.
   */
  readonly resolving = computed(
    () =>
      !this.isSuperAdmin() &&
      this.activePartner.activePartnerId() !== null &&
      this.resource.isLoading(),
  );

  /**
   * Which whole-page message to show, or `null` to render the app.
   *
   * The superAdmin check sits above the payload rather than relying on the
   * `admin` state being non-blocking. Belt and braces: a backend that returned
   * `no_role` for a superAdmin browsing someone else's partner area must not be
   * able to lock them out of it.
   */
  readonly blockedBy = computed<BlockingMembershipState | null>(() => {
    if (this.isSuperAdmin()) {
      return null;
    }
    const state = this.state();
    return isBlockingMembershipState(state) ? state : null;
  });

  /** Whether a whole-page message replaces the app. */
  readonly blocked = computed(() => this.blockedBy() !== null);

  /**
   * Re-ask the API — for a "check again" affordance after someone has been told
   * to go and get a role.
   *
   * Deliberately not wired into `CapabilitiesService.refresh()`: they are
   * separate concerns, and coupling them re-creates exactly the entanglement
   * this service was split out to avoid.
   */
  reload(): void {
    this.resource.reload();
  }
}
